import React from "react";
import { useTranslation } from "react-i18next";
import { ClipboardList, ShieldCheck, BadgeCheck, Download, ArrowRight } from "lucide-react";

const LabelHowToSection = () => {
  const { t } = useTranslation();

  const stepData = [
    {
      icon: ClipboardList,
      title: t("EmiratesMade.Label.titles.1", { defaultValue: "Apply" }),
      text: t("EmiratesMade.Label.steps.1", {
        defaultValue: "Submit your application with company and product details.",
      }),
    },
    {
      icon: ShieldCheck,
      title: t("EmiratesMade.Label.titles.2", { defaultValue: "Verification" }),
      text: t("EmiratesMade.Label.steps.2", {
        defaultValue:
          "Undergo verification and compliance review based on quality and origin.",
      }),
    },
    {
      icon: BadgeCheck,
      title: t("EmiratesMade.Label.titles.3", { defaultValue: "Certification" }),
      text: t("EmiratesMade.Label.steps.3", {
        defaultValue:
          "Upon approval, receive certification and the right to display the Emirates Made label on eligible products.",
      }),
    },
  ];

  const btn = {
    display: "inline-block",
    backgroundColor: "var(--uae-green)",
    color: "#fff",
    textDecoration: "none",
    padding: "12px 24px",
    borderRadius: 9999,
    fontWeight: 700,
    marginRight: 10,
  };

  return (
    <section id="label" style={{ padding: "80px 10%", background: "linear-gradient(180deg,#f7fbf9 0%,#ffffff 85%)" }}>
      {/* Local styles for responsive stepper */}
      <style>{`
        .label-card{background:#fff;border-radius:18px;box-shadow:0 14px 34px rgba(0,0,0,.08);padding:30px 28px;max-width:1200px;margin:0 auto}
        .label-steps{position:relative;display:grid;grid-template-columns:repeat(3,1fr);gap:28px;align-items:start}
        .label-line{display:none}
        .label-topline{position:relative;display:grid;grid-template-columns:repeat(3,1fr);align-items:center;height:20px;margin:6px 0 18px}
        /* Line should start at first dot center and end at last dot center */
        .label-topline::before{content:"";position:absolute;left:calc(16.6667%);right:calc(16.6667%);top:10px;height:4px;border-radius:3px;background:linear-gradient(90deg,var(--uae-green),var(--uae-red));opacity:.4}
        .label-dot{position:relative;justify-self:center;width:16px;height:16px;border-radius:50%;background:#fff;border:3px solid rgba(90,62,200,.35);box-shadow:0 2px 6px rgba(0,0,0,.08)}
        .label-circle{width:86px;height:86px;margin:0 auto;border-radius:50%;background:#fff;border:4px solid #e6f5f1;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 16px rgba(0,0,0,.06);z-index:1;transition:box-shadow .25s ease,transform .25s ease}
        .label-circle:hover{transform:scale(1.03);box-shadow:0 0 0 6px #e6f5f1 inset,0 10px 20px rgba(0,0,0,.10)}
        .label-title{margin-top:14px;margin-bottom:6px;color:#0a2750;font-weight:800;font-size:18px;text-align:center}
        .label-desc{margin:0 auto;color:#475569;line-height:1.75;max-width:360px;text-align:center}
        .label-ctas{display:flex;flex-wrap:wrap;gap:14px;align-items:center;justify-content:center}
        .label-step-cta{margin-top:14px;display:flex;justify-content:center}
        .btn-primary{display:inline-flex;align-items:center;gap:10px;padding:12px 20px;border-radius:9999px;background:var(--uae-green);color:#fff;text-decoration:none;font-weight:700;box-shadow:0 8px 16px rgba(90,62,200,.25);transition:transform .2s ease,box-shadow .2s ease,background .2s ease}
        .btn-primary:hover{transform:translateY(-2px);box-shadow:0 12px 22px rgba(90,62,200,.28);background:#4a33a8}
        .btn-secondary{display:inline-flex;align-items:center;gap:10px;padding:12px 20px;border-radius:9999px;background:#0a2750;color:#fff;text-decoration:none;font-weight:700;box-shadow:0 8px 16px rgba(10,39,80,.18);transition:transform .2s ease,box-shadow .2s ease,opacity .2s ease}
        .btn-secondary:hover{transform:translateY(-2px);box-shadow:0 12px 22px rgba(10,39,80,.22);opacity:.95}
        @media (max-width: 900px){
          .label-card{padding:22px}
          .label-steps{grid-template-columns:1fr;row-gap:26px}
          .label-topline{grid-template-columns:repeat(3,1fr);margin:6px 0 14px}
          .label-circle{margin-left:calc(50% - 42px)}
          .label-title,.label-desc{text-align:center}
          .label-ctas{justify-content:center}
        }
      `}</style>
      <div className="label-card">
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: "#0a2750", margin: 0 }}>
            {t("EmiratesMade.Label.title", { defaultValue: "How to Obtain the Label" })}
          </h2>
          <div style={{ width: 64, height: 6, borderRadius: 3, background: "var(--uae-green)", marginTop: 8 }} />
        </div>

        {/* Stepper */}
        <div style={{ padding: "24px 8px 8px" }}>
          <div className="label-topline" aria-hidden="true">
            {stepData.map((_, idx) => (
              <span key={idx} className="label-dot" />
            ))}
          </div>
          <div className="label-steps">
            {stepData.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i}>
                  <div className="label-circle"><Icon size={28} color="#0a2750" /></div>
                  <h3 className="label-title">{`${i + 1}. ${s.title}`}</h3>
                  <p className="label-desc">{s.text}</p>
                  
                </div>
              );
            })}
          </div>
        </div>

        {/* CTAs */}
        <div className="label-ctas" style={{ marginTop: 22 }} role="group" aria-label="Label application actions">
          <a href="#download" className="btn-primary">
            <Download size={18}/> {t("EmiratesMade.Label.cta1", { defaultValue: "Download Application Form" })}
          </a>
          <a href="#apply-now" className="btn-secondary">
            {t("EmiratesMade.Label.cta2", { defaultValue: "Apply Now" })} <ArrowRight size={18}/>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LabelHowToSection;
